/**
 * Step 1 quantity row: horizontal scroll + PDP-style arrows + scroll indicator (max-width 749px).
 * Legacy rail thumb (non–Flock-v2) or `nw-qty-step__progress` (template a-b-test-flock-armor-v2).
 * Figma reference: mobile quantity slider (785:6660).
 */
(function () {
  const mqMobile = window.matchMedia('(max-width: 749px)');
  // The v3 buy box carries .pdp-v3-buy-box on its section wrapper. It is also still
  // on <body> for the four original templates; querySelector matches either, so a
  // duplicated template keeps the arrow sync.
  const isV3BuyBox = !!document.querySelector('.pdp-v3-buy-box');

  function trackHasHorizontalOverflow(track) {
    if (!track) return false;
    return track.scrollWidth > track.clientWidth + 1;
  }

  function syncQtyArrowsToTrack(viewport, track) {
    if (!viewport || !track) return;
    const arrows = viewport.querySelector('.nw-qty-step__arrows.product__main-image-arrows');
    if (!arrows) return;

    const shouldSync =
      isV3BuyBox &&
      (mqMobile.matches || trackHasHorizontalOverflow(track));
    if (!shouldSync) {
      arrows.style.removeProperty('--qty-track-top');
      arrows.style.removeProperty('--qty-track-height');
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();
    if (!viewportRect.height || !trackRect.height) return;

    const trackTop = Math.max(0, trackRect.top - viewportRect.top);
    arrows.style.setProperty('--qty-track-top', `${trackTop}px`);
    arrows.style.setProperty('--qty-track-height', `${trackRect.height}px`);
  }

  function getScrollStepPx(track) {
    const first = track.querySelector('.nw-qty-step__option');
    if (!first) return 96;
    const gapRaw = getComputedStyle(track).gap || getComputedStyle(track).columnGap || '0';
    const gap = parseFloat(String(gapRaw).split(' ')[0], 10) || 0;
    return Math.max(1, Math.round(first.getBoundingClientRect().width + gap));
  }

  function scrollStep(track, direction) {
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll < 1) return;

    const step = getScrollStepPx(track);
    const target =
      direction > 0
        ? Math.min(track.scrollLeft + step, maxScroll)
        : Math.max(track.scrollLeft - step, 0);

    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  function updateThumb(track, rail, thumb) {
    if (!track || !rail || !thumb) return;
    if (!mqMobile.matches && !trackHasHorizontalOverflow(track)) {
      thumb.style.width = '';
      thumb.style.transform = '';
      return;
    }

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const railWidth = rail.clientWidth;

    if (maxScroll < 0.5 || railWidth <= 0) {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
      return;
    }

    const visibleRatio = track.clientWidth / Math.max(1, track.scrollWidth);
    const thumbWidth = Math.max(Math.round(railWidth * visibleRatio), 20);
    const maxLeft = Math.max(0, railWidth - thumbWidth);
    const t = maxScroll < 0.5 ? 0 : track.scrollLeft / maxScroll;
    const left = t * maxLeft;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translate3d(${left}px,0,0)`;
  }

  /**
   * Flock Armor v2: same scroll math as the legacy thumb, applied to the new progress indicator.
   * CSS transitions apply when `.is-scrolling` is off (e.g. arrow smooth scroll); instant follow when on.
   */
  function syncProgressScrollableClass(track, progressBox) {
    if (!progressBox) return;
    progressBox.classList.toggle('nw-qty-step__progress--scrollable', trackHasHorizontalOverflow(track));
  }

  function updateProgressIndicator(track, progressBox, indicator) {
    if (!track || !progressBox || !indicator) return;
    if (!trackHasHorizontalOverflow(track)) {
      indicator.style.width = '';
      indicator.style.transform = '';
      return;
    }

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const railWidth = progressBox.clientWidth;

    if (maxScroll < 0.5 || railWidth <= 0) {
      indicator.style.width = '100%';
      indicator.style.transform = 'translate3d(0,0,0)';
      return;
    }

    const visibleRatio = track.clientWidth / Math.max(1, track.scrollWidth);
    const thumbWidth = Math.max(Math.round(railWidth * visibleRatio), 20);
    const maxLeft = Math.max(0, railWidth - thumbWidth);
    const t = maxScroll < 0.5 ? 0 : track.scrollLeft / maxScroll;
    const left = t * maxLeft;

    indicator.style.width = `${thumbWidth}px`;
    indicator.style.transform = `translate3d(${left}px,0,0)`;
  }

  function bindViewport(viewport) {
    if (viewport.dataset.nwQtyStepSliderBound === 'true') return;

    const track = viewport.querySelector('[data-nw-qty-step-track]');
    const rail = viewport.querySelector('[data-nw-qty-rail]');
    const thumb = viewport.querySelector('[data-nw-qty-rail-thumb]');
    const progressBox = viewport.querySelector('[data-nw-qty-progress]');
    const progressIndicator = viewport.querySelector('[data-nw-qty-progress-indicator]');
    const prevBtn = viewport.querySelector('[data-nw-qty-step-nav="prev"]');
    const nextBtn = viewport.querySelector('[data-nw-qty-step-nav="next"]');

    if (!track || !prevBtn || !nextBtn) return;

    // Theme base.css has `div:empty { display: none; }`. Keep a child node to avoid hiding the slider thumb/indicator
    // after ProductInfo re-renders variant-selects on option changes.
    if (thumb && thumb.childElementCount === 0) {
      thumb.appendChild(document.createElement('span'));
    }
    if (progressIndicator && progressIndicator.childElementCount === 0) {
      progressIndicator.appendChild(document.createElement('span'));
    }

    viewport.dataset.nwQtyStepSliderBound = 'true';

    let programmaticScrollUntil = 0;
    /** After touch, allow scroll-driven indicator updates without CSS transition (momentum included). */
    let recentTouchMomentumUntil = 0;
    let userScrollIdleTimer = 0;

    const refresh = () => {
      if (progressBox && progressIndicator) {
        syncProgressScrollableClass(track, progressBox);
        updateProgressIndicator(track, progressBox, progressIndicator);
      } else {
        updateThumb(track, rail, thumb);
      }
      syncQtyArrowsToTrack(viewport, track);
    };

    function bumpUserDrivenScrollMode() {
      if (!progressBox || Date.now() < programmaticScrollUntil) return;
      progressBox.classList.add('is-scrolling');
      clearTimeout(userScrollIdleTimer);
      userScrollIdleTimer = setTimeout(() => {
        progressBox.classList.remove('is-scrolling');
        userScrollIdleTimer = 0;
        refresh();
      }, 120);
    }

    function noteTouchActivity() {
      recentTouchMomentumUntil = Date.now() + 900;
      bumpUserDrivenScrollMode();
    }

    /* Coalesce `scroll` only; `touchmove` must call refresh() every time so the thumb follows the finger. */
    let scrollSyncRaf = 0;
    const scheduleScrollSync = () => {
      if (scrollSyncRaf) return;
      scrollSyncRaf = requestAnimationFrame(() => {
        scrollSyncRaf = 0;
        if (
          progressBox &&
          Date.now() < recentTouchMomentumUntil &&
          Date.now() >= programmaticScrollUntil
        ) {
          bumpUserDrivenScrollMode();
        }
        refresh();
      });
    };

    const refreshTouch = () => {
      noteTouchActivity();
      refresh();
    };

    let dragPointerId = null;
    let dragStartClientX = 0;
    let dragStartClientY = 0;
    let dragStartScrollLeft = 0;
    let dragHorizontalActive = false;
    let dragDocMove = null;
    let dragDocEnd = null;

    function removePointerDragListeners() {
      if (!dragDocMove) return;
      document.removeEventListener('pointermove', dragDocMove);
      document.removeEventListener('pointerup', dragDocEnd);
      document.removeEventListener('pointercancel', dragDocEnd);
      dragDocMove = null;
      dragDocEnd = null;
    }

    track.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'touch') return;
        if (!trackHasHorizontalOverflow(track)) return;
        if (e.button !== 0) return;

        dragPointerId = e.pointerId;
        dragStartClientX = e.clientX;
        dragStartClientY = e.clientY;
        dragStartScrollLeft = track.scrollLeft;
        dragHorizontalActive = false;

        dragDocMove = (ev) => {
          if (ev.pointerId !== dragPointerId) return;
          const dx = ev.clientX - dragStartClientX;
          const dy = ev.clientY - dragStartClientY;

          if (!dragHorizontalActive) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            if (Math.abs(dy) >= Math.abs(dx)) {
              removePointerDragListeners();
              dragPointerId = null;
              return;
            }
            dragHorizontalActive = true;
            track.classList.add('nw-qty-step__scroll-row--dragging');
            recentTouchMomentumUntil = Date.now() + 900;
            bumpUserDrivenScrollMode();
          }

          ev.preventDefault();
          track.scrollLeft = dragStartScrollLeft - dx;
          scheduleScrollSync();
        };

        dragDocEnd = (ev) => {
          if (ev.pointerId !== dragPointerId) return;
          removePointerDragListeners();
          const didDrag = dragHorizontalActive;
          dragPointerId = null;
          dragHorizontalActive = false;
          track.classList.remove('nw-qty-step__scroll-row--dragging');

          if (didDrag) {
            const suppressClick = (cev) => {
              cev.preventDefault();
              cev.stopPropagation();
              track.removeEventListener('click', suppressClick, true);
            };
            track.addEventListener('click', suppressClick, true);
          }
        };

        document.addEventListener('pointermove', dragDocMove, { passive: false });
        document.addEventListener('pointerup', dragDocEnd);
        document.addEventListener('pointercancel', dragDocEnd);
      },
      { passive: true }
    );

    track.addEventListener('scroll', scheduleScrollSync, { passive: true });
    track.addEventListener('touchstart', noteTouchActivity, { passive: true });
    track.addEventListener('touchmove', refreshTouch, { passive: true });
    track.addEventListener(
      'touchend',
      () => {
        recentTouchMomentumUntil = Date.now() + 900;
        bumpUserDrivenScrollMode();
        requestAnimationFrame(() => {
          requestAnimationFrame(refresh);
        });
      },
      { passive: true }
    );
    track.addEventListener('scrollend', () => {
      bumpUserDrivenScrollMode();
      scheduleScrollSync();
    }, { passive: true });

    window.addEventListener('resize', scheduleScrollSync, { passive: true });

    if (typeof mqMobile.addEventListener === 'function') {
      mqMobile.addEventListener('change', scheduleScrollSync);
    } else {
      mqMobile.addListener(scheduleScrollSync);
    }

    const markProgrammaticScroll = () => {
      programmaticScrollUntil = Date.now() + 750;
      recentTouchMomentumUntil = 0;
      if (progressBox) {
        progressBox.classList.remove('is-scrolling');
        clearTimeout(userScrollIdleTimer);
        userScrollIdleTimer = 0;
      }
    };

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      markProgrammaticScroll();
      scrollStep(track, -1);
    });

    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      markProgrammaticScroll();
      scrollStep(track, 1);
    });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(scheduleScrollSync);
      ro.observe(track);
      if (rail) ro.observe(rail);
      if (progressBox) ro.observe(progressBox);
    }

    track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener(
        'load',
        () => {
          requestAnimationFrame(refresh);
        },
        { once: true }
      );
    });

    function scheduleInitialRefreshes() {
      scheduleScrollSync();
      requestAnimationFrame(() => {
        scheduleScrollSync();
        requestAnimationFrame(() => {
          refresh();
          requestAnimationFrame(refresh);
        });
      });
      [0, 50, 150, 400].forEach((ms) => setTimeout(refresh, ms));
    }

    scheduleInitialRefreshes();

    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => {
        requestAnimationFrame(refresh);
      });
    }
  }

  function scan(root) {
    const base = root && typeof root.querySelectorAll === 'function' ? root : document;
    base.querySelectorAll('.nw-qty-step__viewport').forEach(bindViewport);
  }

  function runScanDocument() {
    scan(document);
  }

  /**
   * ProductInfo replaces the entire <variant-selects> when options change. New nodes never get
   * the Step 1 slider listeners; re-run bindViewport after the DOM swap (same pattern as
   * shopify:section:load for the theme editor).
   */
  function scanAfterProductInfoUpdate() {
    requestAnimationFrame(() => {
      scan(document);
      requestAnimationFrame(() => {
        scan(document);
        requestAnimationFrame(() => {
          scan(document);
          if (typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new Event('resize'));
          }
        });
      });
    });
  }

  function initNwQtyStepSlider() {
    runScanDocument();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNwQtyStepSlider);
  } else {
    initNwQtyStepSlider();
  }

  document.addEventListener('product-info:loaded', (event) => {
    const root = event.target && event.target.nodeName === 'PRODUCT-INFO' ? event.target : document;
    scan(root);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && typeof event.target.querySelectorAll === 'function') {
      scan(event.target);
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, scanAfterProductInfoUpdate);
  }
})();
